import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, Microscope, TrendingUp } from "lucide-react";
import { evidenceIntro, mechanisms, references, slideImages } from "@/data/evidence";
import Seo from "@/components/Seo";

export default function Wissenschaft() {
  return (
    <div className="grain-bg">
      <Seo
        title="Wissenschaftliche Evidenz - Deep Dive"
        description="31 referenzierte Publikationen zur Wiederherstellung der Lactose-Toleranz. Mechanismen, Studienlage, Mikrobiom-Adaptation."
        path="/wissenschaft"
        keywords="Lactoseintoleranz Studien, Mikrobiom Bifidobacterium, Toleranz Adaptation, evidenzbasierte Ernährung"
      />
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10 lg:pt-24">
        <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3 inline-flex items-center gap-2">
          <Microscope className="w-3.5 h-3.5" /> Deep Dive
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">Wissenschaftliche Evidenz</h1>
        <p className="mt-6 text-lg text-slate-600 max-w-3xl leading-relaxed">
          Hintergründe, Mechanismen und der komplette Literaturüberblick zur Wiederherstellung der Lactose-Toleranz - für alle, die es genauer wissen wollen.
        </p>
      </section>

      {/* Intro */}
      <section className="max-w-4xl mx-auto px-6 py-8 space-y-5">
        {evidenceIntro.map((p, i) => (
          <p key={i} className="text-slate-700 text-lg leading-relaxed">{p}</p>
        ))}
      </section>

      {/* Figures from slides */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="max-w-2xl mb-10">
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Abbildungen</div>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">Grafiken aus der Fachpräsentation</h2>
          <p className="mt-3 text-slate-600">Auszüge aus der Präsentation "Rethinking Lactose Intolerance" - Mechanismen, Studien und Handlungsempfehlungen im Bild.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {slideImages.map((src, i) => (
            <figure key={src} className="rounded-2xl overflow-hidden border border-slate-100 bg-white card-lift" data-testid={`slide-fig-${i + 1}`}>
              <img src={src} alt={`Abbildung ${i + 1}`} loading="lazy" className="w-full aspect-[16/9] object-cover" />
              <figcaption className="px-4 py-3 text-xs text-slate-500 uppercase tracking-wide">Abbildung {i + 1}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Mechanisms */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Mechanismen</div>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">Was die Studien belegen</h2>
        </div>
        <div className="space-y-6">
          {mechanisms.map((m, i) => (
            <Card key={i} className="p-7 rounded-2xl border-slate-100 card-lift">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0 mt-1">
                  <TrendingUp className="w-5 h-5 text-emerald-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 text-lg tracking-tight">{m.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{m.text}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {m.refs.map((r) => (
                      <a key={r} href={`#ref-${r}`} className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 text-xs font-medium text-slate-600 transition-colors">
                        [{r}]
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Bibliography */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-10 flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-emerald-700" />
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Literatur</div>
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">{references.length} referenzierte Publikationen</h2>
          </div>
        </div>
        <ol className="space-y-3">
          {references.map((r) => (
            <li key={r.id} id={`ref-${r.id}`} className="flex gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
              <span className="font-mono text-xs text-emerald-700 font-semibold shrink-0 pt-0.5">[{r.id}]</span>
              <div className="text-sm text-slate-700 leading-relaxed">
                {r.text}
                {r.doi && (
                  <a href={`https://doi.org/${r.doi}`} target="_blank" rel="noreferrer" className="ml-2 text-emerald-700 hover:underline text-xs">
                    doi: {r.doi}
                  </a>
                )}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-slate-100 p-8 lg:p-12 text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900">Bereit für die praktische Umsetzung?</h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">
            Alles, was die Forschung sagt - konkret aufbereitet als 12-Wochen-Plan mit Rezepten, Mengenangaben und wöchentlicher Steigerung.
          </p>
          <Link to="/paywall" className="inline-block mt-6">
            <Button className="rounded-full h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white btn-lift">
              12-Wochen-Plan freischalten <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
